import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getPosLocationCurrency, toFormatPrice } from "../common/helpers/function";
import totalsalesvg from "../../assets/images/totalsalesvg.svg";
import ionWalletOutline from "../../assets/images/ion_wallet-outline.svg";
import customerssvg from "../../assets/images/customerssvg.svg";
import productcardsvg from "../../assets/images/productcardsvg.svg";
import TotalSalesGraphModal from "./TotalSalesGraphModal";
import CustomerGraphModal from "./CustomerGraphModal";
import ProductGraphModal from "./ProductGraphModal";
import "./dashboardChartJsSetup";
import {
  buildPaymentDonutGradient,
  formatPaymentSegmentMeta,
  formatTrendPercent,
  mergeKpiDashboardData,
  resolveDonutCenterLabel,
} from "./kpiDashboardModel";
import "./KpiOverview.scss";

/** i18n keys for payment legend rows when segment has no `label` */
const PAYMENT_LABEL_FALLBACK = [
  { key: "Cash", defaultValue: "Cash" },
  { key: "Bank", defaultValue: "Bank" },
  { key: "Credit card", defaultValue: "Credit card" },
  { key: "Credit note", defaultValue: "Credit note" },
];

const TealIconWrap = ({ children, label }) => (
  <span className="pos-dashboard-kpi-card__icon-wrap" aria-hidden={!label} title={label}>
    {children}
  </span>
);

const TrendBadge = ({ up, value }) => (
  <span className={`pos-dashboard-kpi-trend ${up ? "pos-dashboard-kpi-trend--up" : "pos-dashboard-kpi-trend--down"}`}>
    {up ? (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path d="M8 4l4 6H4l4-6z" fill="currentColor" />
      </svg>
    ) : (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path d="M8 12l-4-6h8l-4 6z" fill="currentColor" />
      </svg>
    )}
    <span>{value}</span>
  </span>
);

const KpiRow = ({ label, value, labelClass,code='' }) => (
  <div className="pos-dashboard-kpi-row">
    <span className={`pos-dashboard-kpi-row__label ${labelClass || ""}`}>{label}</span>
    <span className="pos-dashboard-kpi-row__value">{value} <span style={{fontSize:'12px'}} className="pos-dashboard-kpi-row__value">{code}</span> </span>
  </div>
);

/** Right chevron — visible on card hover (Sale / Customer / Product) */
const KpiTitleChevron = () => (
  <span className="pos-dashboard-kpi-card__title-chevron" aria-hidden>
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

/**
 * KPI cards for POS dashboard. Optional `data` merges with `DEFAULT_KPI_DASHBOARD_DATA` (see ./kpiDashboardModel.js).
 * @param {object} props
 * @param {Date | null} [props.startDate]
 * @param {Date | null} [props.endDate]
 * @param {object | null} [props.data] API payload (any subset of branches: totalSale, payment, customer, product).
 */
const KpiOverview = ({ startDate, endDate, data: dataProp, modalData }) => {
  const { t } = useTranslation();
  const [totalSalesOpen, setTotalSalesOpen] = useState(false);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const vsPrev = t("vsPreviousPeriod", { defaultValue: "Vs previous period" });

  const data = useMemo(() => mergeKpiDashboardData(dataProp), [dataProp]);

  const currencyCode = getPosLocationCurrency();

  const saleMain = toFormatPrice(data.totalSale.mainAmount);
  const saleGross = toFormatPrice(data.totalSale.grossAmount);
  const saleDisc = toFormatPrice(data.totalSale.discountAmount);
  const payCenter = resolveDonutCenterLabel(data.payment);
  const paymentGradient = useMemo(() => buildPaymentDonutGradient(data.payment.segments), [data.payment.segments]);

  const totalSaleTrend = formatTrendPercent(data.totalSale.vsPreviousPercent);
  const customerTrend = formatTrendPercent(data.customer.vsPreviousPercent);
  const productTrend = formatTrendPercent(data.product.vsPreviousPercent);

  const peopleUnit = t("people", { defaultValue: "people" });

  return (
    <section
      className="pos-dashboard-kpi"
      aria-label={t("kpiOverviewSection", { defaultValue: "KPI Overview" })}
    >
      <div className="pos-dashboard-kpi__grid">
        {/* Total sale */}
        <article className="pos-dashboard-kpi-card pos-dashboard-kpi-card--sale">
          <header className="pos-dashboard-kpi-card__head">
            <div className="pos-dashboard-kpi-card__head-text">
              <button
                type="button"
                className="pos-dashboard-kpi-card__title-trigger"
                onClick={() => setTotalSalesOpen(true)}
                aria-label={t("Open total sales details", { defaultValue: "Open total sales details" })}
              >
                <div className="pos-dashboard-kpi-card__title-row">
                  <span className="pos-dashboard-kpi-card__title">{t("TOTAL SALE", { defaultValue: "TOTAL SALE" })}</span>
                  <KpiTitleChevron />
                </div>
              </button>
              <div className="pos-dashboard-kpi-card__metric">
                <span className="pos-dashboard-kpi-card__metric-value">{saleMain}</span>
                <span className="pos-dashboard-kpi-card__metric-unit">{currencyCode}</span>
              </div>
            </div>
            <TealIconWrap label={t("Sales", { defaultValue: "Sales" })}>
              <img src={productcardsvg} alt="" className="pos-dashboard-kpi-card__icon-img" width={24} height={24} decoding="async" />
            </TealIconWrap>
          </header>
          <div className="pos-dashboard-kpi-card__trend-block">
            <span className="pos-dashboard-kpi-card__vs" style={{fontSize:'14px'}}>{vsPrev}</span>
            <TrendBadge up={data.totalSale.vsPreviousUp} value={totalSaleTrend} />
          </div>
          <div className="pos-dashboard-kpi-card__rows">
            <KpiRow
              label={t("Gross sale", { defaultValue: "Gross sale" })}
              value={`${saleGross}`}
              code ={currencyCode}
            />
            <KpiRow
              label={t("Discount", { defaultValue: "Discount" })}
              value={`${saleDisc}`}
              code ={currencyCode}
              labelClass="pos-dashboard-kpi-row__label--warn"
            />
            <KpiRow
              label={t("Total sale", { defaultValue: "Total sale" })}
              value={`${saleMain}`}
              code ={currencyCode}
              labelClass="pos-dashboard-kpi-row__label--brand"
            />
          </div>
        </article>

        {/* Payment method */}
        <article className="pos-dashboard-kpi-card pos-dashboard-kpi-card--payment">
          <header className="pos-dashboard-kpi-card__head pos-dashboard-kpi-card__head--simple">
            <h3 className="pos-dashboard-kpi-card__title">{t("PAYMENT METHOD", { defaultValue: "PAYMENT METHOD" })}</h3>
            <TealIconWrap label={t("Payment", { defaultValue: "Payment" })}>
              <img src={ionWalletOutline} alt="" className="pos-dashboard-kpi-card__icon-img" width={24} height={24} decoding="async" />
            </TealIconWrap>
          </header>
          <div className="pos-dashboard-kpi-payment__body">
            <div className="pos-dashboard-kpi-donut" aria-hidden>
              <div className="pos-dashboard-kpi-donut__ring" style={{ background: paymentGradient }} />
              <div className="pos-dashboard-kpi-donut__hole" />
              <span className="pos-dashboard-kpi-donut__label">{payCenter}</span>
            </div>
            <div className="pos-dashboard-kpi-payment__legend">
              {data.payment.segments.map((seg, i) =>{
                const { main, pctLabel } = formatPaymentSegmentMeta(seg);
                return (
                <div className="pos-dashboard-kpi-leg" key={`payment-seg-${i}-${seg.color ?? i}`}>
                  <span className="pos-dashboard-kpi-leg__dot" style={{ background: seg.color }} />
                  <span className="pos-dashboard-kpi-leg__name">
                    {seg.label != null && seg.label !== ""
                      ? seg.label
                      : t(PAYMENT_LABEL_FALLBACK[i]?.key ?? "Other", {
                          defaultValue: PAYMENT_LABEL_FALLBACK[i]?.defaultValue ?? "Other",
                        })}
                  </span>
                  <div style={{display:'flex',alignItems:'center',gap:'5px'}}>
                     <span className="pos-dashboard-kpi-leg__meta">{main}</span>
                     <span style={{fontSize:'12px'}} className="pos-dashboard-kpi-leg__meta">({pctLabel})</span>
                    </div>
            
                </div>
              )
              })}
            </div>
          </div>
          <div
            className="pos-dashboard-kpi-payment__refund"
            role="group"
            aria-label={t("refundCash", { defaultValue: "Refund cash" })}
          >
            <span className="pos-dashboard-kpi-payment__refund-label">
              {t("refundCash", { defaultValue: "Refund cash" })}
            </span>
            <span className="pos-dashboard-kpi-payment__refund-value">
              {toFormatPrice(Number(data.payment.refundAmount) || 0)}
            </span>
          </div>
        </article>

        {/* Customer */}
        <article className="pos-dashboard-kpi-card pos-dashboard-kpi-card--customer">
          <header className="pos-dashboard-kpi-card__head">
            <div className="pos-dashboard-kpi-card__head-text">
              <button
                type="button"
                className="pos-dashboard-kpi-card__title-trigger"
                onClick={() => setCustomerModalOpen(true)}
                aria-label={t("Open customer details", { defaultValue: "Open customer details" })}
              >
                <div className="pos-dashboard-kpi-card__title-row">
                  <span className="pos-dashboard-kpi-card__title">{t("CUSTOMER", { defaultValue: "CUSTOMER" })}</span>
                  <KpiTitleChevron />
                </div>
              </button>
              <div className="pos-dashboard-kpi-card__metric">
                <span className="pos-dashboard-kpi-card__metric-value">{data.customer.totalPeople}</span>
                <span className="pos-dashboard-kpi-card__metric-unit">{t("People", { defaultValue: "People" })}</span>
              </div>
            </div>
            <TealIconWrap label={t("Customers", { defaultValue: "Customers" })}>
              <img src={customerssvg} alt="" className="pos-dashboard-kpi-card__icon-img" width={24} height={24} decoding="async" />
            </TealIconWrap>
          </header>
          <div className="pos-dashboard-kpi-card__trend-block">
            <span className="pos-dashboard-kpi-card__vs" style={{fontSize:'14px'}}>{vsPrev}</span>
            <TrendBadge up={data.customer.vsPreviousUp} value={customerTrend} />
          </div>
          <div className="pos-dashboard-kpi-card__rows">
            <KpiRow
              label={t("Return customer", { defaultValue: "Return customer" })}
              value={`${data.customer.returnCount}`}
              code={peopleUnit}
            />
            <KpiRow
              label={t("New customer", { defaultValue: "New customer" })}
              value={`${data.customer.newCount}`}
              code={peopleUnit}
            />
            <KpiRow
              label={t("AVG spent", { defaultValue: "AVG spent" })}
              value={`${toFormatPrice(data.customer.avgSpentPerPerson)}`}
              code={`/${t("person", { defaultValue: "person" })}`}
            />
          </div>
        </article>

        {/* Product */}
        <article className="pos-dashboard-kpi-card pos-dashboard-kpi-card--product">
          <header className="pos-dashboard-kpi-card__head">
            <div className="pos-dashboard-kpi-card__head-text">
              <button
                type="button"
                className="pos-dashboard-kpi-card__title-trigger"
                onClick={() => setProductModalOpen(true)}
                aria-label={t("Open product details", { defaultValue: "Open product details" })}
              >
                <div className="pos-dashboard-kpi-card__title-row">
                  <span className="pos-dashboard-kpi-card__title">{t("PRODUCT", { defaultValue: "PRODUCT" })}</span>
                  <KpiTitleChevron />
                </div>
              </button>
              <div className="pos-dashboard-kpi-card__metric">
                <span className="pos-dashboard-kpi-card__metric-value">{data.product.totalPcs}</span>
                <span className="pos-dashboard-kpi-card__metric-unit">{t("Pcs", { defaultValue: "Pcs" })}</span>
              </div>
            </div>
            <TealIconWrap label={t("Products", { defaultValue: "Products" })}>
              <img src={totalsalesvg} alt="" className="pos-dashboard-kpi-card__icon-img" width={24} height={24} decoding="async" />
            </TealIconWrap>
          </header>
          <div className="pos-dashboard-kpi-card__trend-block">
            <span className="pos-dashboard-kpi-card__vs" style={{fontSize:'14px'}}>{vsPrev}</span>
            <TrendBadge up={data.product.vsPreviousUp} value={productTrend} />
          </div>
          <div className="pos-dashboard-kpi-card__rows">
            <KpiRow label={t("POS", { defaultValue: "POS" })} value={String(data.product.posCount)} />
            <KpiRow label={t("Custom Order", { defaultValue: "Custom Order" })} value={String(data.product.customOrderCount)} />
            <KpiRow
              label={t("Exchange/Refund", { defaultValue: "Exchange/Refund" })}
              value={String(data.product.exchangeRefundCount)}
            />
          </div>
        </article>
      </div>
      <TotalSalesGraphModal
        open={totalSalesOpen}
        onClose={() => setTotalSalesOpen(false)}
        startDate={startDate}
        endDate={endDate}
        data={modalData?.totalSale}
      />
      <CustomerGraphModal
        open={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        startDate={startDate}
        endDate={endDate}
        data={modalData?.customer}
      />
      <ProductGraphModal
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        startDate={startDate}
        endDate={endDate}
        data={modalData?.product}
      />
    </section>
  );
};

export default KpiOverview;
